import { curveLinear } from "@visx/curve";
import { ParentSize } from "@visx/responsive";
import {
  AreaSeries,
  Axis,
  DataContext,
  Grid,
  LineSeries,
  Tooltip,
  XYChart,
  buildChartTheme,
} from "@visx/xychart";
import { useContext, useMemo, type CSSProperties } from "react";
import type { PayoffPoint } from "../lib/composePayoff";
import { findBreakevens } from "../lib/scenarios";

const TOTAL_COLOR = "#eef1f5";
const PERP_COLOR = "#a78bfa";
const PRED_COLOR = "#60a5fa";
const GRID = "rgba(194,214,255,0.06)";

const AXIS_LABEL = {
  fill: "rgba(194,214,255,0.4)",
  fontSize: 11,
  fontFamily: "var(--f-mono, ui-monospace, monospace)",
  fontWeight: 400,
} as const;

const theme = buildChartTheme({
  backgroundColor: "transparent",
  colors: [TOTAL_COLOR, PERP_COLOR, PRED_COLOR],
  gridColor: GRID,
  gridColorDark: GRID,
  gridStyles: { strokeDasharray: "4 4" },
  tickLength: 0,
  svgLabelSmall: AXIS_LABEL,
  svgLabelBig: AXIS_LABEL,
});

const MARGIN = { top: 28, right: 24, bottom: 32, left: 74 };

// --------------------------------------------------------------------------
// Accessors
// --------------------------------------------------------------------------
function xAcc(d: PayoffPoint): number {
  return d.price;
}
function totalAcc(d: PayoffPoint): number {
  return d.totalPnl;
}
function perpAcc(d: PayoffPoint): number {
  return d.perpPnl;
}
function predAcc(d: PayoffPoint): number {
  return d.predPnl;
}
function posAcc(d: PayoffPoint): number {
  return Math.max(0, d.totalPnl);
}
function negAcc(d: PayoffPoint): number {
  return Math.min(0, d.totalPnl);
}
function zeroAcc(): number {
  return 0;
}

function formatUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? "−" : ""}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${n < 0 ? "−" : ""}$${(abs / 1000).toFixed(1)}K`;
  if (abs >= 1000)
    return n.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 10_000) return `$${(n / 1000).toFixed(1)}K`;
  if (n >= 1000) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toPrecision(3)}`;
}

// --------------------------------------------------------------------------
// Overlays
// --------------------------------------------------------------------------

function VerticalLine({
  value,
  stroke,
  dash,
  label,
  labelOffsetY = 0,
}: {
  value: number;
  stroke: string;
  dash?: string;
  label?: string;
  /** Optional vertical offset to stack labels that would otherwise collide. */
  labelOffsetY?: number;
}) {
  const { xScale, margin, innerHeight, innerWidth } = useContext(DataContext);
  if (!xScale || !margin) return null;
  const fn = xScale as unknown as (v: number) => number;
  const x = fn(value);
  if (x === undefined || Number.isNaN(x)) return null;
  const y1 = margin.top;
  const y2 = margin.top + (innerHeight ?? 0);

  // Keep labels inside the plot: near edges, flip anchor.
  const plotRight = margin.left + (innerWidth ?? 0);
  const edgePad = 4;
  let anchor: "start" | "middle" | "end" = "middle";
  let labelX = x;
  if (x < margin.left + 28) {
    anchor = "start";
    labelX = x + edgePad;
  } else if (x > plotRight - 28) {
    anchor = "end";
    labelX = x - edgePad;
  }

  return (
    <g>
      <line
        x1={x}
        x2={x}
        y1={y1}
        y2={y2}
        stroke={stroke}
        strokeDasharray={dash ?? "4 4"}
        strokeWidth={1}
      />
      {label && (
        <text
          x={labelX}
          y={y1 - 8 - labelOffsetY}
          fill={stroke}
          fontFamily="var(--f-mono, monospace)"
          fontSize={9}
          textAnchor={anchor}
          letterSpacing="0.14em"
        >
          {label}
        </text>
      )}
    </g>
  );
}

function ZeroLine() {
  const { yScale, margin, innerWidth } = useContext(DataContext);
  if (!yScale || !margin) return null;
  const fn = yScale as unknown as (v: number) => number;
  const y = fn(0);
  if (y === undefined || Number.isNaN(y)) return null;
  return (
    <line
      x1={margin.left}
      x2={margin.left + (innerWidth ?? 0)}
      y1={y}
      y2={y}
      stroke="rgba(194,214,255,0.35)"
      strokeWidth={1}
    />
  );
}

function BreakevenMarker({ price }: { price: number }) {
  const { xScale, yScale, margin } = useContext(DataContext);
  if (!xScale || !yScale || !margin) return null;
  const xFn = xScale as unknown as (v: number) => number;
  const yFn = yScale as unknown as (v: number) => number;
  const cx = xFn(price);
  const cy = yFn(0);
  if (Number.isNaN(cx) || Number.isNaN(cy)) return null;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={3.5}
        fill="var(--bg-0)"
        stroke="var(--fg-max)"
        strokeWidth={1.25}
      />
      <text
        x={cx}
        y={cy - 10}
        fill="var(--fg-hi)"
        fontFamily="var(--f-mono, monospace)"
        fontSize={9}
        letterSpacing="0.12em"
        textAnchor="middle"
      >
        BE {formatPrice(price)}
      </text>
    </g>
  );
}

function FloatingReadout({
  price,
  totalPnl,
  label,
  accent,
}: {
  price: number;
  totalPnl: number;
  label: string;
  accent?: string;
}) {
  const { xScale, yScale, margin, innerWidth } = useContext(DataContext);
  if (!xScale || !yScale || !margin) return null;
  const xFn = xScale as unknown as (v: number) => number;
  const yFn = yScale as unknown as (v: number) => number;
  const cx = xFn(price);
  const cy = yFn(totalPnl);
  if (Number.isNaN(cx) || Number.isNaN(cy)) return null;

  const color = accent ?? (totalPnl >= 0 ? "var(--up)" : "var(--dn)");

  const text = `${label.toUpperCase()} ${formatUsd(totalPnl)}`;
  const width = Math.max(92, text.length * 7 + 16);
  const plotRight = margin.left + (innerWidth ?? 0);
  const flip = cx + 8 + width > plotRight;
  const tx = flip ? cx - 8 - width : cx + 8;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="var(--bg-0)"
        stroke={color}
        strokeWidth={1.5}
      />
      <g transform={`translate(${tx}, ${cy})`}>
        <rect
          x={0}
          y={-10}
          width={width}
          height={20}
          fill="rgba(8,9,11,0.9)"
          stroke={`${color}88`}
          rx={2}
        />
        <text
          x={8}
          y={0}
          dominantBaseline="middle"
          fill={color}
          fontFamily="var(--f-mono, monospace)"
          fontSize={11}
          fontWeight={500}
          letterSpacing="0.04em"
        >
          {text}
        </text>
      </g>
    </g>
  );
}

function interpolateTotal(
  points: ReadonlyArray<PayoffPoint>,
  price: number,
): number {
  if (points.length === 0) return 0;
  if (price <= points[0]!.price) return points[0]!.totalPnl;
  const last = points[points.length - 1]!;
  if (price >= last.price) return last.totalPnl;
  let lo = points[0]!;
  let hi = last;
  for (let i = 1; i < points.length; i++) {
    if (points[i]!.price >= price) {
      hi = points[i]!;
      lo = points[i - 1]!;
      break;
    }
  }
  if (hi.price === lo.price) return lo.totalPnl;
  const t = (price - lo.price) / (hi.price - lo.price);
  return lo.totalPnl + t * (hi.totalPnl - lo.totalPnl);
}

// --------------------------------------------------------------------------
// Main chart
// --------------------------------------------------------------------------

export function CompositeChart({
  points,
  mark,
  threshold,
  liquidation,
  highlight,
}: {
  points: ReadonlyArray<PayoffPoint>;
  mark: number;
  threshold: number;
  liquidation?: number;
  /** Optional scenario-highlight overlay: vertical line + floating readout. */
  highlight?: { price: number; label?: string } | null;
}) {
  const xDomain = useMemo((): [number, number] | undefined => {
    if (points.length === 0) return undefined;
    return [points[0]!.price, points[points.length - 1]!.price];
  }, [points]);

  const yDomain = useMemo((): [number, number] | undefined => {
    if (points.length === 0) return undefined;
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of points) {
      if (p.totalPnl < lo) lo = p.totalPnl;
      if (p.perpPnl < lo) lo = p.perpPnl;
      if (p.predPnl < lo) lo = p.predPnl;
      if (p.totalPnl > hi) hi = p.totalPnl;
      if (p.perpPnl > hi) hi = p.perpPnl;
      if (p.predPnl > hi) hi = p.predPnl;
    }
    if (lo > 0) lo = 0;
    if (hi < 0) hi = 0;
    const range = hi - lo || Math.abs(hi) * 0.1 || 1;
    const pad = range * 0.1;
    return [lo - pad, hi + pad];
  }, [points]);

  const breakevens = useMemo(() => findBreakevens(points), [points]);
  const atMark = useMemo(
    () => interpolateTotal(points, mark),
    [points, mark],
  );
  const highlightTotal = useMemo(
    () => (highlight ? interpolateTotal(points, highlight.price) : 0),
    [points, highlight],
  );
  const highlightInRange =
    highlight &&
    xDomain &&
    highlight.price >= xDomain[0] &&
    highlight.price <= xDomain[1];

  const wrapperStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
  };

  if (points.length === 0 || !xDomain || !yDomain) {
    return <div style={wrapperStyle} />;
  }

  const liqInRange =
    liquidation !== undefined &&
    isFinite(liquidation) &&
    liquidation > xDomain[0] &&
    liquidation < xDomain[1];

  return (
    <div style={wrapperStyle}>
      <ParentSize>
        {({ width, height }) => {
          if (width === 0 || height === 0) return null;
          return (
            <XYChart
              theme={theme}
              width={width}
              height={height}
              margin={MARGIN}
              xScale={{ type: "linear", domain: xDomain, zero: false }}
              yScale={{ type: "linear", domain: yDomain, zero: false }}
            >
              {/* Gradients for area fills */}
              <defs>
                <linearGradient id="pos-gradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgba(108, 207, 142, 0.22)" />
                  <stop offset="100%" stopColor="rgba(108, 207, 142, 0.03)" />
                </linearGradient>
                <linearGradient id="neg-gradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgba(224, 106, 106, 0.03)" />
                  <stop offset="100%" stopColor="rgba(224, 106, 106, 0.22)" />
                </linearGradient>
              </defs>

              <Grid
                columns={false}
                numTicks={5}
                lineStyle={{ stroke: GRID }}
              />

              {/* Shaded areas */}
              <AreaSeries
                dataKey="pos"
                data={points as PayoffPoint[]}
                xAccessor={xAcc}
                yAccessor={posAcc}
                y0Accessor={zeroAcc}
                fill="url(#pos-gradient)"
                stroke="transparent"
                curve={curveLinear}
              />
              <AreaSeries
                dataKey="neg"
                data={points as PayoffPoint[]}
                xAccessor={xAcc}
                yAccessor={negAcc}
                y0Accessor={zeroAcc}
                fill="url(#neg-gradient)"
                stroke="transparent"
                curve={curveLinear}
              />

              {/* Per-leg faint overlays */}
              <LineSeries
                dataKey="perp"
                data={points as PayoffPoint[]}
                xAccessor={xAcc}
                yAccessor={perpAcc}
                stroke={PERP_COLOR}
                strokeOpacity={0.4}
                strokeWidth={1.25}
                curve={curveLinear}
              />
              <LineSeries
                dataKey="pred"
                data={points as PayoffPoint[]}
                xAccessor={xAcc}
                yAccessor={predAcc}
                stroke={PRED_COLOR}
                strokeOpacity={0.4}
                strokeWidth={1.25}
                curve={curveLinear}
              />

              {/* Composite total — bold, linear curve so the kink at the
                  prediction threshold renders as a near-vertical segment
                  (composePayoff injects the straddling pair). */}
              <LineSeries
                dataKey="total"
                data={points as PayoffPoint[]}
                xAccessor={xAcc}
                yAccessor={totalAcc}
                stroke={TOTAL_COLOR}
                strokeWidth={2}
                curve={curveLinear}
              />

              {/* Reference overlays */}
              <ZeroLine />
              {liqInRange && (
                <VerticalLine
                  value={liquidation!}
                  stroke="var(--sev-crit)"
                  dash="2 3"
                  label="LIQ"
                />
              )}
              <VerticalLine
                value={mark}
                stroke="rgba(194,214,255,0.55)"
                label="MARK"
              />
              <VerticalLine
                value={threshold}
                stroke="rgba(96,165,250,0.75)"
                label="THRESHOLD"
                dash="6 3"
                labelOffsetY={
                  Math.abs(threshold - mark) / Math.max(mark, 1) < 0.02 ? 12 : 0
                }
              />

              {breakevens.map((p) => (
                <BreakevenMarker key={p} price={p} />
              ))}

              {highlightInRange && highlight!.price !== mark && (
                <VerticalLine
                  value={highlight!.price}
                  stroke="var(--brand)"
                  dash="3 2"
                />
              )}
              <FloatingReadout
                price={mark}
                totalPnl={atMark}
                label="AT MARK"
              />
              {highlightInRange && (
                <FloatingReadout
                  price={highlight!.price}
                  totalPnl={highlightTotal}
                  label={highlight!.label ?? "SCENARIO"}
                  accent="var(--brand)"
                />
              )}

              <Axis
                orientation="bottom"
                numTicks={5}
                tickFormat={formatPrice}
                hideTicks
                hideAxisLine
                tickLabelProps={() => ({ dy: 6, fontSize: 11 })}
              />
              <Axis
                orientation="left"
                numTicks={5}
                tickFormat={formatUsd}
                hideTicks
                hideAxisLine
                tickLabelProps={() => ({ dx: -6, fontSize: 11 })}
              />

              <Tooltip
                showVerticalCrosshair
                verticalCrosshairStyle={{
                  stroke: "rgba(194,214,255,0.35)",
                  strokeWidth: 1,
                }}
                snapTooltipToDatumX
                unstyled
                applyPositionStyle
                renderTooltip={({ tooltipData }) => {
                  const datum = tooltipData?.nearestDatum?.datum as
                    | PayoffPoint
                    | undefined;
                  if (!datum) return null;
                  return (
                    <div
                      style={{
                        background: "rgba(8, 9, 11, 0.9)",
                        border: `1px solid ${TOTAL_COLOR}55`,
                        borderRadius: 2,
                        padding: "6px 10px",
                        fontFamily: "var(--f-mono)",
                        fontSize: 11,
                        color: "var(--fg-hi)",
                        pointerEvents: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          letterSpacing: "0.14em",
                          color: "var(--fg-meta)",
                        }}
                      >
                        {formatPrice(datum.price)}
                      </div>
                      <Row
                        label="PERP"
                        value={formatUsd(datum.perpPnl)}
                        color={PERP_COLOR}
                      />
                      <Row
                        label="PRED"
                        value={formatUsd(datum.predPnl)}
                        color={PRED_COLOR}
                      />
                      <Row
                        label="TOTAL"
                        value={formatUsd(datum.totalPnl)}
                        color={
                          datum.totalPnl >= 0 ? "var(--up)" : "var(--dn)"
                        }
                        bold
                      />
                    </div>
                  );
                }}
              />
            </XYChart>
          );
        }}
      </ParentSize>
    </div>
  );
}

function Row({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: string;
  color: string;
  bold?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        justifyContent: "space-between",
        minWidth: 140,
      }}
    >
      <span
        style={{
          fontSize: 9,
          letterSpacing: "0.14em",
          color: "var(--fg-dim)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          color,
          fontWeight: bold ? 600 : 400,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}
